import "./index.css";
import { clsx } from "clsx";
import type { HTMLAttributes, ReactElement } from "react";
import { isValidElement, useId, useMemo } from "react";
import { useControlledValue } from "../../hooks/useControlledValue";
import { Slot } from "../../utils";
import { Dropdown } from "../Dropdown";
import type { IPopoverProps } from "./types";

export const Popover = (props: IPopoverProps) => {
  const {
    trigger,
    children,
    content,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    defaultIsOpen,
    placement = "bottom-start",
    offset = 8,
    maxWidth = 320,
    contentClassName,
    // Dropdown-specific props
    disabled,
    preventDefaultOnTrigger,
    stopPropagationOnTrigger,
    middleware,
    flip,
    shift,
    autoPlacement,
    clickOptions,
    hoverOptions,
    focusOptions,
    dismissOptions,
    roleOptions,
    listNavigationOptions,
    typeaheadOptions,
    floatingFocusManager: floatingFocusManagerProp,
    ariaLabel,
    ariaLabelledby,
    animation,
    scrollable,
    preserveTabOrder,
    triggerId: triggerIdProp,
    contentId: contentIdProp,
    minWidth,
    minHeight,
    maxHeight,
    matchTriggerWidth,
    typeaheadListRef,
    // Remaining props forwarded to the trigger element
    ...forwardedProps
  } = props;

  const { value: open, onChange: onOpenChange } = useControlledValue<boolean>({
    controlledValue: controlledOpen,
    controlledOnChange: controlledOnOpenChange,
    defaultValue: defaultIsOpen,
  });

  const generatedId = useId();
  const triggerId =
    children.props.id || triggerIdProp || `${generatedId}-trigger`;
  const contentId =
    contentIdProp ||
    (isValidElement(content) &&
    (content as ReactElement<HTMLAttributes<HTMLElement>>).props.id
      ? (content as ReactElement<HTMLAttributes<HTMLElement>>).props.id
      : `${generatedId}-content`);

  const triggerElementBase = trigger || children;

  const triggerElement = useMemo(() => {
    return <Slot {...forwardedProps}>{triggerElementBase}</Slot>;
  }, [forwardedProps, triggerElementBase]);

  return (
    <Dropdown
      trigger={triggerElement}
      content={content}
      triggerId={triggerId}
      contentId={contentId}
      open={open}
      onOpenChange={onOpenChange}
      disabled={disabled}
      preventDefaultOnTrigger={preventDefaultOnTrigger}
      stopPropagationOnTrigger={stopPropagationOnTrigger}
      middleware={middleware}
      flip={flip}
      shift={shift}
      autoPlacement={autoPlacement}
      clickOptions={clickOptions}
      hoverOptions={hoverOptions}
      focusOptions={focusOptions}
      dismissOptions={dismissOptions}
      roleOptions={roleOptions}
      listNavigationOptions={listNavigationOptions}
      typeaheadOptions={typeaheadOptions}
      floatingFocusManager={{
        initialFocus: 0,
        restoreFocus: true,
        ...floatingFocusManagerProp,
      }}
      ariaLabel={ariaLabel}
      ariaLabelledby={ariaLabelledby}
      animation={animation}
      scrollable={scrollable}
      preserveTabOrder={preserveTabOrder}
      minWidth={minWidth}
      maxWidth={maxWidth}
      minHeight={minHeight}
      maxHeight={maxHeight}
      matchTriggerWidth={matchTriggerWidth}
      typeaheadListRef={typeaheadListRef}
      interactions={["click", "dismiss"]}
      role="dialog"
      placement={placement}
      offset={offset}
      contentClassName={clsx("nuco-popover__content", contentClassName)}
    />
  );
};
